import { computeSalesLedger } from './salesLedger.mjs';

// This module deliberately has no Firebase, HTTP, or clock dependency.  The
// server supplies the ledger and the authorised scope; consumers can therefore
// reproduce every number from the returned audit records.
const asList = (value) => Array.isArray(value)
  ? value.filter(Boolean)
  : Object.entries(value && typeof value === 'object' ? value : {})
    .map(([id, record]) => record && typeof record === 'object' ? { id: record.id ?? id, ...record } : null)
    .filter(Boolean);

const numberId = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))
  ? Number(value)
  : null;
const sameId = (left, right) => numberId(left) !== null && numberId(left) === numberId(right);
const text = (value) => value == null ? '' : String(value).trim();
const UNKNOWN_COMMERCIAL_ORIGIN = 'Origem não identificada';

function explicitCommercialOrigin(record) {
  if (!record || typeof record !== 'object') return '';
  const source = record.source && typeof record.source === 'object' ? record.source : {};
  return text(record.origin || record.origem || record.origem_canal || source.channel);
}

function canonicalSaleOrigin(sale, sourceRecord) {
  if (sale.recordType === 'lift') return text(sale.origin) || UNKNOWN_COMMERCIAL_ORIGIN;
  // A Lead/Prospecção sale already carries an immutable commercial source from
  // the ledger. Direct client sales need an explicit source on their source
  // record; the ledger's "Atendimento Presencial" fallback is not evidence.
  if (sale.recordType === 'lead') return 'Lead';
  if (sale.recordType === 'prospeccao') return 'Prospecção';
  return explicitCommercialOrigin(sourceRecord) || UNKNOWN_COMMERCIAL_ORIGIN;
}

function commercialOriginBucket(origin) {
  const normalized = text(origin).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (normalized === 'lead') return 'lead';
  if (normalized === 'indicacao') return 'indicacao';
  if (normalized === 'venda em outra loja') return 'cross_store';
  if (normalized.includes('prospec')) return 'prospeccao';
  if (normalized.includes('porta') || normalized.includes('presencial')) return 'presencial';
  if (normalized === 'origem nao identificada' || !normalized) return 'unknown';
  return 'other';
}

export function canonicalLeadEntryDate(record) {
  return record?.data_criacao
    || record?.createdAt
    || record?.created_at
    || record?.data
    || record?.date
    || '';
}

function nestedEventList(value) {
  return asList(value).flatMap(record => {
    if (record?.event_type) return [record];
    return asList(record);
  });
}

export function saoPauloDateKey(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pieces = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  const result = Object.fromEntries(pieces.filter(({ type }) => type !== 'literal').map(({ type, value: part }) => [type, part]));
  return `${result.year}-${result.month}-${result.day}`;
}

export function canonicalPeriod({ start, end } = {}) {
  const valid = (value) => /^\d{4}-\d{2}-\d{2}$/.test(text(value)) && saoPauloDateKey(`${value}T12:00:00-03:00`) === value;
  if (!valid(start) || !valid(end) || start > end) throw new RangeError('Período inválido; use YYYY-MM-DD com início menor ou igual ao fim.');
  return { start, end, timezone: 'America/Sao_Paulo' };
}

export function isInCanonicalPeriod(value, period) {
  const key = /^\d{4}-\d{2}-\d{2}$/.test(text(value)) ? text(value).slice(0, 10) : saoPauloDateKey(value);
  return Boolean(key && key >= period.start && key <= period.end);
}

function recordDate(record, fields) {
  return fields.map((field) => record?.[field]).find((value) => text(value)) || null;
}

function storeFor(record, users) {
  return numberId(record?.storeId ?? record?.store_id)
    ?? numberId(users.find((user) => sameId(user.id, record?.consultantId))?.storeId)
    ?? 1;
}

function scoped(records, { storeId, consultantId }, users) {
  return asList(records).filter((record) => (
    (storeId == null || storeFor(record, users) === Number(storeId))
    && (consultantId == null || sameId(record.consultantId ?? record.first_consultant_id, consultantId))
  ));
}

function auditRecord(type, record, date, users, extra = {}) {
  const consultantId = numberId(record.consultantId ?? record.first_consultant_id);
  const user = users.find(candidate => sameId(candidate.id, consultantId));
  return {
    id: `${type}:${record.id ?? record.event_id ?? record.phone ?? 'unknown'}`,
    type,
    date: date || null,
    storeId: storeFor(record, users),
    consultantId,
    consultantName: record.consultantName || record.consultorNome || record.first_consultant_name || user?.name || null,
    name: record.name || record.nome || null,
    phone: record.phone || record.telefone || null,
    source: sourceOf(record),
    campaign: campaignOf(record),
    ad: text(record.anuncio_nome || record.ad_name || record.marketing_ad_name || record.criativo || record.produto_anuncio || record.anuncio_id || record.ad_id) || 'Não informado',
    adId: text(record.anuncio_id || record.ad_id || record.meta_source_id || record.source_id) || null,
    category: text(record.categoria || record.category || record.produto_categoria || record.produto) || 'Não informada',
    channel: channelOf(record),
    clientType: clientTypeOf(record),
    status: record.status || record.aurora_status || record.stage || record.estagio || null,
    stage: record.stage || record.estagio || record.status || null,
    attributionQuality: record.attribution_quality || null,
    attributionClass: record.attribution_class || null,
    attributionSnapshotId: record.attribution_snapshot_id || null,
    attributionHierarchy: record.attribution_hierarchy || null,
    ...extra,
  };
}

function sourceOf(record) {
  const source = record.source && typeof record.source === 'object' ? record.source : {};
  return text(record.marketing_source_name || record.origem_canal || record.origem || source.channel || 'Não informado') || 'Não informado';
}

function campaignOf(record) {
  const source = record.source && typeof record.source === 'object' ? record.source : {};
  return text(record.campaign_id || record.marketing_campaign_name || record.campaign_id_reported || record.campanha || source.campaign_id || 'Não informada') || 'Não informada';
}

function channelOf(record) {
  const explicit = text(record.canal || record.channel || record.origem_canal);
  if (explicit) return explicit;
  const source = sourceOf(record).toLowerCase();
  if (source.includes('instagram')) return 'Instagram';
  if (source.includes('facebook') || source.includes('meta')) return 'Facebook';
  if (source.includes('whatsapp')) return 'WhatsApp';
  if (source.includes('porta') || source.includes('presencial')) return 'Presencial';
  return 'Não informado';
}

function clientTypeOf(record) {
  const explicit = text(record.clientType || record.tipo_cliente || record.type);
  if (explicit) return explicit;
  const origin = text(record.origin || record.origem).toLowerCase();
  if (origin.includes('prospec')) return 'prospeccao';
  if (origin.includes('lead')) return 'lead';
  if (origin.includes('atendimento') || origin.includes('porta') || origin.includes('presencial')) return 'presencial';
  return record.telefone || record.phone ? 'lead' : 'outro';
}

function snapshotList(value) {
  return nestedEventList(value).filter(record => record?.snapshot_type === 'lead_origin'
    && record.snapshot_version === 1 && record.lead_id && record.store_id != null);
}

function receivedAttribution(snapshot) {
  const values = snapshot?.received_attribution;
  return values && typeof values === 'object' ? values : {};
}

function attributionProjection(record, snapshots) {
  if (!record || !record.id) return record;
  const snapshot = snapshots.get(`${storeFor(record, [] )}:${record.id}`)
    || snapshots.get(`${numberId(record.storeId ?? record.store_id)}:${record.id}`);
  if (!snapshot) return record;
  const values = receivedAttribution(snapshot);
  const provider = snapshot.received_provider_evidence && typeof snapshot.received_provider_evidence === 'object'
    ? snapshot.received_provider_evidence : {};
  // Do not manufacture a channel: only actual captured values replace mutable
  // lead fields. The quality remains visible even when no identifiers arrived.
  return {
    ...record,
    ...(values.campaign_id ? { campaign_id: values.campaign_id } : {}),
    ...(values.utm_campaign && !values.campaign_id ? { campaign_id: values.utm_campaign } : {}),
    ...(values.ad_id ? { ad_id: values.ad_id } : {}),
    ...(values.creative_id && !values.ad_id ? { ad_id: values.creative_id } : {}),
    ...(provider.source_name ? { marketing_source_name: provider.source_name } : {}),
    ...(provider.campaign_name && !values.campaign_id && !values.utm_campaign ? { marketing_campaign_name: provider.campaign_name } : {}),
    ...(provider.ad_name || provider.ad_title ? { marketing_ad_name: provider.ad_name || provider.ad_title } : {}),
    attribution_quality: snapshot.evidence_quality || 'unknown',
    attribution_class: snapshot.attribution_class || 'legacy',
    attribution_snapshot_id: snapshot.snapshot_id || null,
    attribution_hierarchy: {
      campaign_id: values.campaign_id || values.utm_campaign || null,
      campaign_name: provider.campaign_name || null,
      adset_id: values.adset_id || null,
      adset_name: provider.adset_name || null,
      ad_id: values.ad_id || null,
      ad_name: provider.ad_name || null,
      creative_id: values.creative_id || null,
      creative_name: provider.creative_name || null,
    },
  };
}

function immutableSaleMarketingEvidence(snapshot) {
  if (!snapshot) return null;
  const values = receivedAttribution(snapshot);
  const provider = snapshot.received_provider_evidence && typeof snapshot.received_provider_evidence === 'object'
    ? snapshot.received_provider_evidence : {};
  const campaign = values.campaign_id || values.utm_campaign || provider.campaign_name || 'Não informada';
  const ad = provider.ad_name || provider.ad_title || values.ad_id || values.creative_id || 'Não informado';
  return {
    source: provider.source_name || 'Origem de marketing não identificada',
    campaign,
    ad,
    adId: values.ad_id || null,
    quality: snapshot.evidence_quality || 'unknown',
    attributionClass: snapshot.attribution_class || 'reported',
    snapshotId: snapshot.snapshot_id || null,
    hierarchy: {
      campaign_id: values.campaign_id || values.utm_campaign || null,
      campaign_name: provider.campaign_name || null,
      adset_id: values.adset_id || null, adset_name: provider.adset_name || null,
      ad_id: values.ad_id || null, ad_name: provider.ad_name || provider.ad_title || null,
      creative_id: values.creative_id || null, creative_name: provider.creative_name || null,
    },
  };
}

function normalizedBooleanFilter(value) {
  if (value === true || value === 'true' || value === 'yes' || value === '1') return true;
  if (value === false || value === 'false' || value === 'no' || value === '0') return false;
  return null;
}

function equalsFilter(actual, expected) {
  if (expected == null || expected === '' || expected === 'all') return true;
  return text(actual).toLowerCase() === text(expected).toLowerCase();
}

export function matchesCanonicalFilters(record, filters = {}) {
  if (!equalsFilter(record.source, filters.origin)) return false;
  if (!equalsFilter(record.campaign, filters.campaign)) return false;
  if (!equalsFilter(record.ad, filters.ad)) return false;
  if (!equalsFilter(record.category, filters.category)) return false;
  if (!equalsFilter(record.channel, filters.channel)) return false;
  if (!equalsFilter(record.clientType, filters.clientType)) return false;
  if (!equalsFilter(record.status, filters.status)) return false;
  if (!equalsFilter(record.stage, filters.stage)) return false;
  const booleanFields = [
    ['servedByAurora', filters.servedByAurora],
    ['transferredByAurora', filters.transferredByAurora],
    ['humanServed', filters.humanServed],
    ['sold', filters.sold],
  ];
  return booleanFields.every(([field, expected]) => {
    const wanted = normalizedBooleanFilter(expected);
    return wanted === null || Boolean(record[field]) === wanted;
  });
}

const rate = (numerator, denominator) => denominator
  ? Math.min(100, Math.round((numerator / denominator) * 1000) / 10)
  : 0;
const average = (values) => {
  const valid = values.filter(value => Number.isFinite(value) && value >= 0);
  return valid.length ? Math.round((valid.reduce((sum, value) => sum + value, 0) / valid.length) * 10) / 10 : null;
};

function isLostRecord(record) {
  return ['desqualificado', 'perdido', 'sem_interesse', 'seminteresse'].includes(
    text(record?.status || record?.estagio || record?.stage || record?.stageId).toLowerCase()
  ) || record?.desqualificado === true;
}

function isReactivatedRecord(record) {
  return record?.reativado === true
    || Boolean(record?.reativado_em || record?.reactivatedAt)
    || ['reativado', 'reativacao'].includes(text(record?.status || record?.estagio || record?.stage).toLowerCase());
}

export function computeCanonicalReporting({
  clients = [], leads = [], prospeccao = [], liftSales = [], reminders = [], handoffEvents = [], humanServiceEvents = [], messages = [],
  reportingEvents = [], leadOriginSnapshots = [], leadOriginSaleLinks = [], marketingJourneyEvents = [], users = [], stores = [], period, scope = {}, filters = {},
  normalizePhone = (value) => String(value || '').replace(/\D/g, ''),
} = {}) {
  const canonical = canonicalPeriod(period);
  const allowed = { storeId: numberId(scope.storeId), consultantId: numberId(scope.consultantId) };
  const inPeriod = (value) => isInCanonicalPeriod(value, canonical);
  const ledger = computeSalesLedger({
    clients: asList(clients), leads: asList(leads), prospeccao: asList(prospeccao), liftSales: asList(liftSales), periodRange: canonical,
    storeId: allowed.storeId, consultantId: allowed.consultantId, inPeriod, normalizePhone, users: asList(users),
    consultantAcrossStores: allowed.consultantId !== null,
  });
  const snapshotByLead = new Map(snapshotList(leadOriginSnapshots)
    .filter(snapshot => allowed.storeId == null || sameId(snapshot.store_id, allowed.storeId))
    .map(snapshot => [`${Number(snapshot.store_id)}:${snapshot.lead_id}`, snapshot]));
  const scopedLeads = scoped(leads, allowed, asList(users)).map(record => (
    attributionProjection(record, snapshotByLead)
  ));
  const scopedClients = scoped(clients, allowed, asList(users));
  const scopedProspeccao = scoped(prospeccao, allowed, asList(users));
  const caseRecords = [...scopedLeads, ...scopedProspeccao, ...scopedClients];
  const caseFor = (storeId, phone) => caseRecords.find(record => (
    storeFor(record, users) === Number(storeId)
    && normalizePhone(record.phone || record.telefone) === normalizePhone(phone)
  ));
  const caseAttributes = (record) => record ? {
    source: sourceOf(record),
    campaign: campaignOf(record),
    channel: channelOf(record),
    clientType: clientTypeOf(record),
    status: record.status || record.aurora_status || record.stage || record.estagio || null,
    stage: record.stage || record.estagio || record.status || null,
    attributionQuality: record.attribution_quality || null,
    attributionSnapshotId: record.attribution_snapshot_id || null,
  } : {};
  const compactEvents = Array.from(new Map(
    scoped(nestedEventList(reportingEvents), allowed, asList(users))
      .filter(record => record.event_version === 1 && record.event_id && record.occurred_at)
      .map(record => [record.event_id, record])
  ).values());
  const compactAudit = (type, record, extra = {}) => auditRecord(type, record, record.occurred_at, users, {
    ...caseAttributes(caseFor(record.store_id ?? record.storeId, record.phone)),
    conversationId: record.conversation_id || null,
    sourceRef: record.source_ref || null,
    evidence: record.evidence_type || null,
    ...extra,
  });
  const leadEntries = scopedLeads
    .filter((record) => !record.manualFechamento && inPeriod(canonicalLeadEntryDate(record)))
    .map((record) => auditRecord('lead_entry', record, canonicalLeadEntryDate(record), users))
    .filter(record => matchesCanonicalFilters(record, filters));
  const scopedHandoffs = scoped(nestedEventList(handoffEvents), allowed, asList(users))
    .filter((record) => record.event_type === 'aurora_handoff')
    .map((record) => auditRecord('aurora_handoff', record, record.transferred_at, users, {
      ...caseAttributes(caseFor(record.store_id ?? record.storeId, record.phone)),
      transferredByAurora: true,
      servedByAurora: true,
      interactionCount: Number(record.interaction_count) || 0,
      decision: record.decision || null,
    }));
  const fullHandoffByPhone = new Map(scopedHandoffs.map((handoff) => [`${handoff.storeId}:${normalizePhone(handoff.phone)}`, handoff]));
  const handoffs = scopedHandoffs
    .filter((record) => inPeriod(record.date))
    .filter(record => matchesCanonicalFilters(record, filters));
  const sessionStarts = compactEvents
    .filter(record => record.event_type === 'aurora_session_started' && inPeriod(record.occurred_at))
    .map(record => compactAudit('aurora_served', record, { servedByAurora: true }))
    .filter(record => matchesCanonicalFilters(record, filters));
  const servedConversationKeys = new Set(sessionStarts.map(record => `${record.storeId}:${record.conversationId || normalizePhone(record.phone)}`));
  const auroraServed = [
    ...sessionStarts,
    ...handoffs
      .filter(record => !servedConversationKeys.has(`${record.storeId}:${record.conversationId || normalizePhone(record.phone)}`))
      .map(record => ({ ...record, id: `${record.id}:served`, type: 'aurora_served' })),
  ];
  const decisionRecords = compactEvents
    .filter(record => record.event_type === 'aurora_decision' && inPeriod(record.occurred_at))
    .map(record => compactAudit('aurora_decision', record, {
      servedByAurora: true,
      decision: text(record.decision).toLowerCase(),
    }))
    .filter(record => matchesCanonicalFilters(record, filters));
  const decidedConversationKeys = new Set(decisionRecords.map(record => `${record.storeId}:${record.conversationId || normalizePhone(record.phone)}`));
  const qualified = [
    ...decisionRecords
      .filter(record => ['qualificado', 'encaminhado_humano'].includes(record.decision))
      .map(record => ({ ...record, type: 'aurora_qualified' })),
    ...handoffs
      .filter(record => (record.decision === 'qualificado' || !record.decision)
        && !decidedConversationKeys.has(`${record.storeId}:${record.conversationId || normalizePhone(record.phone)}`))
      .map(record => ({ ...record, id: `${record.id}:qualified`, type: 'aurora_qualified' })),
  ];
  const disqualified = decisionRecords
    .filter(record => ['desqualificado', 'prospeccao_futura'].includes(record.decision))
    .map(record => ({ ...record, type: 'aurora_disqualified' }));
  const services = scoped(nestedEventList(humanServiceEvents), allowed, asList(users))
    .filter(record => record.event_type === 'aurora_human_service' && inPeriod(record.occurred_at))
    .map(record => auditRecord('human_service', record, record.occurred_at, users, {
      ...caseAttributes(caseFor(record.store_id ?? record.storeId, record.phone)),
      handoffId: record.handoff_event_id || null,
      evidence: record.evidence || 'provider_accepted_human_message',
      servedByAurora: true,
      transferredByAurora: true,
      humanServed: true,
    }))
    .filter(service => {
      const handoff = fullHandoffByPhone.get(`${service.storeId}:${normalizePhone(service.phone)}`);
      return handoff && new Date(service.date).getTime() >= new Date(handoff.date).getTime();
    });
  const filteredServices = services.filter(record => matchesCanonicalFilters(record, filters));
  const compactMessages = compactEvents
    .filter(record => ['inbound_message', 'outbound_message', 'aurora_message'].includes(record.event_type))
    .map(record => compactAudit(record.event_type, record, {
      servedByAurora: record.event_type === 'aurora_message',
      messageDirection: record.message_direction || (record.event_type === 'inbound_message' ? 'inbound' : 'outbound'),
      actorType: record.actor_type || null,
    }));
  const conversationsByKey = compactMessages.reduce((result, record) => {
    const key = `${record.storeId}:${record.conversationId || normalizePhone(record.phone)}`;
    const group = result.get(key) || [];
    group.push(record);
    result.set(key, group);
    return result;
  }, new Map());
  const responseCases = [];
  const unanswered = [];
  for (const records of conversationsByKey.values()) {
    const ordered = records.slice().sort((left, right) => String(left.date).localeCompare(String(right.date)));
    const inbound = ordered.filter(record => record.messageDirection === 'inbound');
    const latestInbound = inbound[inbound.length - 1];
    if (!latestInbound || !inPeriod(latestInbound.date)) continue;
    const response = ordered.find(record => (
      record.messageDirection === 'outbound' && new Date(record.date).getTime() >= new Date(latestInbound.date).getTime()
    ));
    if (response) {
      responseCases.push({
        ...response,
        id: `first_response:${response.id}`,
        type: 'first_response',
        firstInboundAt: latestInbound.date,
        responseMinutes: (new Date(response.date).getTime() - new Date(latestInbound.date).getTime()) / 60000,
      });
    } else {
      unanswered.push({ ...latestInbound, id: `unanswered:${latestInbound.id}`, type: 'unanswered_conversation' });
    }
  }
  const filteredResponseCases = responseCases.filter(record => matchesCanonicalFilters(record, filters));
  const filteredUnanswered = unanswered.filter(record => matchesCanonicalFilters(record, filters));
  const visits = compactEvents
    .filter(record => record.event_type === 'visit_completed' && inPeriod(record.occurred_at))
    .map(record => compactAudit('visit', record, { visitCompleted: true }))
    .filter(record => matchesCanonicalFilters(record, filters));
  const followUps = scoped(reminders, allowed, asList(users))
    .filter((record) => inPeriod(recordDate(record, ['followup_at', 'date', 'dueDate', 'createdAt'])))
    .map((record) => auditRecord('follow_up', record, recordDate(record, ['followup_at', 'date', 'dueDate', 'createdAt']), users))
    .filter(record => matchesCanonicalFilters(record, filters));
  const saleLinkBySale = new Map(nestedEventList(leadOriginSaleLinks)
    .filter(link => link?.link_type === 'lead_origin_sale' && link.link_version === 1 && link.sale_id && link.lead_id)
    .filter(link => allowed.storeId == null || sameId(link.store_id, allowed.storeId))
    .map(link => [`${Number(link.store_id)}:${link.sale_id}`, link]));
  const sales = ledger.sales.map((sale, index) => {
    // The scoped API only sends value/store/seller for another store. Never
    // reconstruct its customer from a local record or return an opaque order ID.
    if (sale.crossStore) return {
      id:`cross_store:${index}`, type:'sale', crossStore:true,
      date:null, storeId:Number(sale.storeId),
      origin:'Venda em outra loja', source:'LIFT', value:Number(sale.value) || 0,
    };
    const sourceRecord = sale.record || sale.client || null;
    const link = saleLinkBySale.get(`${Number(sale.storeId)}:${sale.id}`);
    const snapshot = link ? snapshotByLead.get(`${Number(sale.storeId)}:${link.lead_id}`) : null;
    const marketingEvidence = immutableSaleMarketingEvidence(snapshot);
    const commercialOrigin = canonicalSaleOrigin(sale, sourceRecord);
    return auditRecord('sale', {
      ...sale,
      id: sale.id,
      storeId: sale.storeId,
      consultantId: sale.consultantId,
      origin: commercialOrigin,
      source: sale.source,
    }, sale.saleDate, users, {
    id: sale.id, type: 'sale', date: sale.saleDate, storeId: sale.storeId, consultantId: sale.consultantId,
    name: sale.name || null, phone: sale.phone || null,
    source: marketingEvidence?.source || 'Origem de marketing não identificada', origin: commercialOrigin,
    commercialOrigin,
      campaign: marketingEvidence?.campaign || 'Não informada',
      ad: marketingEvidence?.ad || 'Não informado',
      adId: marketingEvidence?.adId || null,
      channel: 'Não informado', clientType: 'outro',
      attributionQuality: marketingEvidence?.quality || 'unidentified',
      attributionClass: marketingEvidence?.attributionClass || 'unidentified',
      attributionSnapshotId: marketingEvidence?.snapshotId || null,
      attributionHierarchy: marketingEvidence?.hierarchy || null,
      attributionSource: marketingEvidence?.source || 'Origem de marketing não identificada',
      attributionCampaign: marketingEvidence?.campaign || 'Não informada',
      attributionAd: marketingEvidence?.ad || 'Não informado',
    value: Number(sale.value) || 0, sold: true,
    });
  }).filter(record => matchesCanonicalFilters(record, filters));
  const presencial = scopedClients
    .filter(record => inPeriod(canonicalLeadEntryDate(record)) && clientTypeOf(record) === 'presencial')
    .map(record => auditRecord('presencial', record, canonicalLeadEntryDate(record), users))
    .filter(record => matchesCanonicalFilters(record, filters));
  const prospecting = scopedProspeccao
    .filter(record => inPeriod(canonicalLeadEntryDate(record)))
    .map(record => auditRecord('prospeccao', record, canonicalLeadEntryDate(record), users))
    .filter(record => matchesCanonicalFilters(record, filters));
  const lost = [...scopedLeads, ...scopedClients, ...scopedProspeccao]
    .filter(record => isLostRecord(record) && inPeriod(record.ultima_atualizacao || record.updatedAt || canonicalLeadEntryDate(record)))
    .map(record => auditRecord('lost', record, record.ultima_atualizacao || record.updatedAt || canonicalLeadEntryDate(record), users))
    .filter(record => matchesCanonicalFilters(record, filters));
  const reactivated = [...scopedLeads, ...scopedClients, ...scopedProspeccao]
    .filter(record => isReactivatedRecord(record) && inPeriod(record.reativado_em || record.reactivatedAt || record.ultima_atualizacao || canonicalLeadEntryDate(record)))
    .map(record => auditRecord('reactivated', record, record.reativado_em || record.reactivatedAt || record.ultima_atualizacao || canonicalLeadEntryDate(record), users))
    .filter(record => matchesCanonicalFilters(record, filters));
  const auroraPhones = new Set(scopedHandoffs.map(record => `${record.storeId}:${normalizePhone(record.phone)}`));
  const auroraSales = sales
    .map(sale => ({ ...sale, servedByAurora: auroraPhones.has(`${sale.storeId}:${normalizePhone(sale.phone)}`), transferredByAurora: auroraPhones.has(`${sale.storeId}:${normalizePhone(sale.phone)}`) }))
    .filter(sale => sale.servedByAurora);
  const filteredRevenue = sales.reduce((sum, sale) => sum + sale.value, 0);
  const originDefinitions = [
    {
      key: 'lead',
      label: 'Leads',
      denominatorLabel: 'Leads recebidos',
      denominatorRecords: leadEntries,
      sales: sales.filter(sale => commercialOriginBucket(sale.origin) === 'lead'),
    },
    {
      key: 'presencial',
      label: 'Atendimento presencial / porta',
      denominatorLabel: 'Atendimentos presenciais',
      denominatorRecords: presencial,
      sales: sales.filter(sale => commercialOriginBucket(sale.origin) === 'presencial'),
    },
    {
      key: 'prospeccao',
      label: 'Prospecção',
      denominatorLabel: 'Clientes/oportunidades prospectados',
      denominatorRecords: prospecting,
      sales: sales.filter(sale => commercialOriginBucket(sale.origin) === 'prospeccao'),
    },
    {
      key: 'indicacao',
      label: 'Indicação',
      denominatorLabel: null,
      denominatorRecords: [],
      sales: sales.filter(sale => commercialOriginBucket(sale.origin) === 'indicacao'),
    },
    {
      key: 'cross_store',
      label: 'Venda em outra loja',
      denominatorLabel: null,
      denominatorRecords: [],
      sales: sales.filter(sale => sale.crossStore),
    },
    {
      key: 'other',
      label: 'Outras origens identificadas',
      denominatorLabel: null,
      denominatorRecords: [],
      sales: sales.filter(sale => commercialOriginBucket(sale.origin) === 'other'),
    },
    {
      key: 'unknown',
      label: UNKNOWN_COMMERCIAL_ORIGIN,
      denominatorLabel: null,
      denominatorRecords: [],
      sales: sales.filter(sale => commercialOriginBucket(sale.origin) === 'unknown'),
    },
  ];
  const originBreakdown = originDefinitions
    .filter(origin => origin.key !== 'indicacao' || origin.sales.length > 0)
    .filter(origin => origin.key !== 'cross_store' || origin.sales.length > 0)
    .filter(origin => origin.key !== 'other' || origin.sales.length > 0)
    .filter(origin => origin.key !== 'unknown' || origin.sales.length > 0)
    .map(origin => {
      const revenue = origin.sales.reduce((sum, sale) => sum + sale.value, 0);
      const denominator = origin.denominatorRecords.length;
      return {
        key: origin.key,
        source: origin.label,
        label: origin.label,
        denominatorLabel: origin.denominatorLabel,
        denominator,
        denominatorReliable: Boolean(origin.denominatorLabel),
        salesCount: origin.sales.length,
        revenue,
        conversion: origin.denominatorLabel && denominator > 0 ? rate(origin.sales.length, denominator) : null,
        revenueShare: rate(revenue, filteredRevenue),
        records: origin.sales,
        denominatorRecords: origin.denominatorRecords,
      };
    });
  const sourceBreakdown = originBreakdown;
  const campaignBreakdown = Object.values(sales.reduce((result, sale) => {
    const key = sale.campaign || 'Não informada';
    const current = result[key] || { campaign: key, salesCount: 0, revenue: 0, records: [] };
    current.salesCount += 1; current.revenue += sale.value; current.records.push(sale); result[key] = current;
    return result;
  }, {}));
  const qualificationRate = rate(qualified.length, auroraServed.length);
  const transferRate = rate(handoffs.length, qualified.length);
  const transferUtilizationRate = rate(filteredServices.length, handoffs.length);
  const auroraConversion = rate(auroraSales.length, auroraServed.length);
  const firstResponseMinutes = filteredServices.map(service => {
    const handoff = scopedHandoffs.find(item => item.id === service.handoffId || (
      item.storeId === service.storeId && normalizePhone(item.phone) === normalizePhone(service.phone)
    ));
    return handoff ? (new Date(service.date).getTime() - new Date(handoff.date).getTime()) / 60000 : NaN;
  });
  const timeToSaleDays = sales.map(sale => {
    const entry = leadEntries.find(item => item.storeId === sale.storeId && normalizePhone(item.phone) === normalizePhone(sale.phone));
    return entry ? (new Date(sale.date).getTime() - new Date(entry.date).getTime()) / 86400000 : NaN;
  });
  const compactAuroraMessages = compactMessages.filter(record => record.type === 'aurora_message' && inPeriod(record.date));
  const interactionCounts = compactAuroraMessages.length
    ? Array.from(conversationsByKey.values()).map(records => records.filter(record => record.type === 'aurora_message' && inPeriod(record.date)).length).filter(Boolean)
    : handoffs.map(record => Number(record.interactionCount)).filter(Number.isFinite);
  const allRecords = [...leadEntries, ...auroraServed, ...qualified, ...disqualified, ...handoffs, ...filteredServices, ...filteredResponseCases, ...filteredUnanswered, ...visits, ...followUps, ...sales, ...presencial, ...prospecting, ...lost, ...reactivated];
  const journeys = Object.values(allRecords.reduce((result, record) => {
    const phone = normalizePhone(record.phone);
    if (!phone) return result;
    const key = `${record.storeId}:${phone}`;
    const journey = result[key] || {
      id: `journey:${key}`,
      type: 'journey',
      storeId: record.storeId,
      consultantId: record.consultantId,
      consultantName: record.consultantName,
      name: record.name,
      phone,
      source: record.source,
      campaign: record.campaign,
      events: [],
    };
    journey.events.push({
      id: record.id,
      type: record.type,
      date: record.date,
      status: record.status,
      evidence: record.evidence || null,
    });
    result[key] = journey;
    return result;
  }, {})).map(journey => ({
    ...journey,
    date: journey.events.map(event => event.date).filter(Boolean).sort()[0] || null,
    events: journey.events.sort((left, right) => String(left.date || '').localeCompare(String(right.date || ''))),
  }));
  const storeCatalog = asList(stores).filter(store => allowed.storeId === null || sameId(store.id, allowed.storeId));
  const consultantCatalog = asList(users).filter(user => (
    user.role === 'consultant'
    && (allowed.storeId === null || sameId(user.storeId, allowed.storeId))
    && (allowed.consultantId === null || sameId(user.id, allowed.consultantId))
  ));
  const comparison = (field, catalog, idField) => {
    const ids = new Set([
      ...catalog.map(item => numberId(item.id)),
      ...allRecords.map(record => numberId(record[field])),
    ].filter(value => value !== null));
    return [...ids].sort((a, b) => a - b).map(id => {
      const records = allRecords.filter(record => sameId(record[field], id));
       const extraCrossStore = field === 'consultantId' && allowed.storeId !== null
         && allowed.consultantId === null && catalog.some(user => sameId(user.id, id)
           && sameId(user.storeId, allowed.storeId))
         ? computeSalesLedger({
             liftSales:asList(liftSales), clients:[], leads:[], prospeccao:[],
             storeId:allowed.storeId, consultantId:id, consultantAcrossStores:true,
             periodRange:canonical, inPeriod, normalizePhone, users:asList(users),
           }).sales.filter(sale => sale.crossStore).map((sale, index) => ({
             id:`cross_store:${id}:${index}`, type:'sale', crossStore:true,
             storeId:Number(sale.storeId), consultantId:id, value:Number(sale.value) || 0,
             origin:'Venda em outra loja', source:'LIFT',
           }))
         : [];
       const groupSales = [
         ...sales.filter(record => sameId(record[field], id)
           || (record.crossStore && field === 'consultantId' && sameId(allowed.consultantId, id))),
         ...extraCrossStore,
       ];
      const groupLeads = leadEntries.filter(record => sameId(record[field], id));
      const item = catalog.find(candidate => sameId(candidate.id, id));
      return {
        [idField]: id,
        name: item?.name || `ID ${id}`,
        leadEntries: groupLeads.length,
        auroraHandoffs: handoffs.filter(record => sameId(record[field], id)).length,
        auroraServed: auroraServed.filter(record => sameId(record[field], id)).length,
        qualified: qualified.filter(record => sameId(record[field], id)).length,
        effectiveHumanServices: filteredServices.filter(record => sameId(record[field], id)).length,
        visits: visits.filter(record => sameId(record[field], id)).length,
        followUps: followUps.filter(record => sameId(record[field], id)).length,
        salesCount: groupSales.length,
        revenue: groupSales.reduce((sum, sale) => sum + sale.value, 0),
        recordCount: records.length,
        records: groupSales,
      };
    });
  };
  const auroraRevenue = auroraSales.reduce((sum, sale) => sum + sale.value, 0);
  const attributionQuality = leadEntries.reduce((result, record) => {
    const key = record.attributionQuality || 'legacy_or_not_captured';
    result[key] = (result[key] || 0) + 1;
    return result;
  }, {});
  const journeyEvents = scoped(nestedEventList(marketingJourneyEvents), allowed, asList(users))
    .filter(event => event.event_version === 1 && inPeriod(event.occurred_at))
    .map(event => auditRecord('marketing_journey', event, event.occurred_at, users, {
      journeyEventType: event.event_type, leadId: event.lead_id || null,
      saleId: event.sale_id || null, evidence: event.evidence_quality || null,
    }));
  const adPerformance = Object.values(sales.reduce((result, sale) => {
    const hierarchy = sale.attributionHierarchy || {};
    const key = hierarchy.ad_id || (sale.attributionAd !== 'Não informado' ? sale.attributionAd : null) || 'Não informado';
    const current = result[key] || {
      adId: hierarchy.ad_id || sale.adId || null,
      adName: hierarchy.ad_name || sale.attributionAd || 'Não informado',
      campaignId: hierarchy.campaign_id || null,
      campaignName: hierarchy.campaign_name || sale.campaign,
      salesCount: 0, revenue: 0, records: [],
    };
    current.salesCount += 1; current.revenue += sale.value; current.records.push(sale);
    result[key] = current;
    return result;
  }, {}));
  return {
    period: canonical, scope: allowed, filters,
    summary: {
      leadEntries: leadEntries.length,
      auroraServed: auroraServed.length,
      qualified: qualified.length,
      auroraDisqualified: disqualified.length,
      qualificationRate,
      auroraHandoffs: handoffs.length,
      transferRate,
      effectiveHumanServices: filteredServices.length,
      transferUtilizationRate,
      averageHandoffToHumanMinutes: average(firstResponseMinutes),
      auroraInteractionCount: compactAuroraMessages.length || interactionCounts.reduce((sum, value) => sum + value, 0),
      averageAuroraInteractionsBeforeHandoff: average(interactionCounts),
      averageFirstResponseMinutes: average(filteredResponseCases.map(record => record.responseMinutes)),
      answeredConversations: filteredResponseCases.length,
      unansweredConversations: filteredUnanswered.length,
      visits: visits.length,
      followUps: followUps.length,
      presencial: presencial.length,
      prospecting: prospecting.length,
      lost: lost.length,
      reactivated: reactivated.length,
      salesCount: sales.length,
      revenue: filteredRevenue,
      ticketMedio: sales.length ? filteredRevenue / sales.length : 0,
      unknownOriginCount: sales.filter(sale => sale.type === 'sale' && !sale.crossStore
        && commercialOriginBucket(sale.origin) === 'unknown').length,
      manualWithoutLiftCount: ledger.manualWithoutLift?.length || 0,
      auroraSalesCount: auroraSales.length,
      auroraRevenue,
      auroraTicketMedio: auroraSales.length ? auroraRevenue / auroraSales.length : 0,
      auroraConversion,
      averageLeadToSaleDays: average(timeToSaleDays),
      attributionQuality,
    },
    sources: sourceBreakdown,
    originBreakdown,
    campaigns: campaignBreakdown,
    marketing: {
      livePeriod: { start: canonical.start, end: canonical.end, leadEntries: leadEntries.length, salesCount: sales.length, revenue: filteredRevenue, attributionQuality },
      hierarchy: leadEntries.map(record => ({
        leadId: record.id.replace(/^lead_entry:/, ''), storeId: record.storeId,
        attributionClass: record.attributionClass || 'legacy',
        attributionQuality: record.attributionQuality || 'legacy_or_not_captured',
        ...(record.attributionHierarchy || {}),
      })),
      adPerformance,
      consultantPerformance: comparison('consultantId', consultantCatalog, 'consultantId'),
    },
    comparisons: {
      stores: comparison('storeId', storeCatalog, 'storeId'),
      consultants: comparison('consultantId', consultantCatalog, 'consultantId'),
    },
    records: {
      leadEntries,
      auroraServed,
      qualified,
      auroraDisqualified: disqualified,
      auroraHandoffs: handoffs,
      effectiveHumanServices: filteredServices,
      firstResponses: filteredResponseCases,
      unansweredConversations: filteredUnanswered,
      visits,
      followUps,
      sales,
      manualWithoutLift: (ledger.manualWithoutLift || []).map(sale => ({
        id:sale.id, type:'manual_without_lift', storeId:sale.storeId,
        consultantId:sale.consultantId, name:sale.name, date:sale.saleDate,
        value:sale.value, warning:sale.warning || 'Venda manual sem pedido no LIFT',
      })),
      auroraSales,
      presencial,
      prospecting,
      lost,
      reactivated,
      journeys,
      journeyEvents,
    },
  };
}